import React, { Component, useState } from "react";

import seedrandom from "./seedrandom";
import QuizLayout from "./widgets/QuizLayout";

import {
  pad2,
  daysInMonth,
  dayStrToParts,
  dayOfWeek,
  dayPartsToStr,
  DayQuestion,
  formatDateStr,
} from "./QuizDay";

import { NthQuestion, solveNth, describeNthQuestion } from "./QuizNth";

import { getLocaleShortWeekday, getLocaleIsRtl } from "./util/localisation";
import { getIsUsa } from "./util/settings";

import Logo from "./ui/Logo";

import "./Daily.css";

const FIRST_DAY = 19496;

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MS_PER_MIN = 60 * 1000;
const DAILY_SWITCH_TIME = 4 * 60 * 60 * 1000; // 4am

const EMOJI_FAIL = "🤦";
const EMOJI_LIFE = "⭐";
const EMOJI_CORRECT = "🟩";
const EMOJI_WRONG = "🟥";
const EMOJI_TICK = "✓";
const EMOJI_CROSS = "✗";
const EMOJI_EMPTY_BLACK = "⬛";
const EMOJI_EMPTY_WHITE = "⬜";
const EMOJI_EMPTY_ORANGE = "🟧";

const EMOJI_BOX_NUMS = [
  "0️⃣",
  "1️⃣",
  "2️⃣",
  "3️⃣",
  "4️⃣",
  "5️⃣",
  "6️⃣",
  "7️⃣",
  "8️⃣",
  "9️⃣",
];
const EMOJI_BOX_NUM_10 = "🔟";

const dayNumToDateInfo = (n) => {
  const date = new Date((FIRST_DAY + n + 0.5) * MS_PER_DAY);

  return {
    year: date.getFullYear(),
    month: date.getMonth(),
    date: date.getDate(),
  };
};

const getAnswer = (questionStr) => {
  if (questionStr.indexOf(":") < 0) {
    const { year, month, date } = dayStrToParts(questionStr);
    return dayOfWeek(year, month, date);
  } else {
    return solveNth(questionStr);
  }
};

const getDayInfo = () => {
  const now = new Date();

  const n =
    (now.getTime() - now.getTimezoneOffset() * MS_PER_MIN - DAILY_SWITCH_TIME) /
      MS_PER_DAY -
    FIRST_DAY;

  const dayNum = Math.min(1e5, Math.max(0, Math.floor(n)));

  return {
    _isOneOffEarly: !dayNum,
    dayFrac: n % 1,
    dayNum: dayNum || 1,
  };
};

const getDisplayFormatRnd = (dayNum, questionIndex) =>
  new seedrandom("FORMAT_" + dayNum + "_" + questionIndex).quick();

export const shuffleArray = (arr, rng) => {
  arr = arr.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const n = Math.floor((rng ? rng.quick() : Math.random()) * (i + 1));
    const t = arr[n];
    arr[n] = arr[i];
    arr[i] = t;
  }
  return arr;
};

const random20thCenturyYear = (rng, todayStr) => {
  return 1901 + Math.floor(rng.quick() * 99);
};

const randomLate21stCenturyYear = (thisYear, rng) => {
  // late 21st century
  const yearLastDigits = Math.max(21, Math.min(50, thisYear % 100)); // 21->50
  const MIN_AHEAD = 20;
  return (
    2000 +
    yearLastDigits +
    MIN_AHEAD +
    Math.floor(rng.quick() * (100 - yearLastDigits - MIN_AHEAD))
  );
};

const generateRandomGame = (todayStr, rng) => {
  const { year } = dayStrToParts(todayStr);

  //const t = Math.random()*10000; for (let i=0; i<t; i++) rng.quick();

  const MONTHS = shuffleArray([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11], rng);
  const DAYS_OF_WEEK = shuffleArray([0, 1, 2, 3, 4, 5, 6], rng);

  const list = [];

  const yearLastDigits = Math.max(21, Math.min(50, year % 100)); // 21->50
  const nearbyYears = shuffleArray([year + 1, year - 1, year - 2], rng);

  const units = shuffleArray([6, 7, 8, 9], rng); // not too low, too easy
  const tens = shuffleArray([10, 11, 12, 13, 14, 15, 16, 17, 18, 19], rng);
  const twenties = shuffleArray([20, 21, 22, 23, 24, 25, 26, 27, 28, 29], rng);
  const thirties = [30, 31];

  const nonUnits = thirties.concat(twenties.splice(0, 5), tens.splice(0, 4));
  // 8 questions use these numbers (first two units, last two are nth of x)
  // choose 11 (possible last question is Feb and can't use 29,30,31);

  const dayNums = shuffleArray(nonUnits, rng);
  dayNums.push(tens.pop());
  dayNums.push(units.pop());

  for (let i = 0; i < 12; i++) {
    const m = MONTHS[i];
    let y;
    let d;
    if (i < 2) {
      // 0,1
      y = year;
      d = dayNums.pop();
    } else if (i < 5) {
      // 2,3,4
      y = nearbyYears[i - 2];
    } else if (i < 8) {
      // 5,6,7
      // early 21st century
      y = year - 3 - Math.floor(rng.quick() * (yearLastDigits - 2));
    } else if (i === 8) {
      // 20th century
      y = random20thCenturyYear(rng, todayStr);
    } else if (i === 9) {
      y = randomLate21stCenturyYear(year, rng);
    } else {
      let y;
      let nth;

      if (i === 10) {
        y = year;
        nth = 1 + Math.floor(rng.quick() * 3); // 1st to 4th
      } else {
        y =
          rng.quick() < 0.5
            ? random20thCenturyYear(rng, todayStr)
            : randomLate21stCenturyYear(year, rng);
        nth = 0; // last
      }

      // last X of nearbyYear
      list.push(
        y + "-" + pad2(m + 1) + "-" + nth + ":" + DAYS_OF_WEEK[i === 10 ? 0 : 1]
      );
      continue;
    }

    if (!d) {
      const maxDaynum = daysInMonth(y, m);
      const dIndex = dayNums.findIndex((n) => n <= maxDaynum);
      d = dayNums[dIndex];
      dayNums.splice(dIndex, 1);
    }

    list.push(dayPartsToStr(y, m, d));
  }

  return list;
};

const rollYearMonth = (year, month, monthDelta) => {
  month += monthDelta;
  if (month > 11) {
    year++;
    month %= 12;
  }
  if (month < 0) {
    year--;
    month += 12;
  }
  return year + "-" + pad2(month + 1);
};

const getPuzzle = (dayNum) => {
  let { year, month, date } = dayNumToDateInfo(dayNum);

  const todayStr = dayPartsToStr(year, month, date);

  const rng = new seedrandom("DAY_" + dayNum);

  let questions;

  const badMonths = [-2, -1, 0, 1, 2].map((offset) =>
    rollYearMonth(year, month, offset)
  );

  let failCount = 0;
  for (; failCount < 100; failCount++) {
    questions = generateRandomGame(todayStr, rng);

    // redo if bad months
    if (
      questions.some((item) =>
        badMonths.some((badMonth) => item.indexOf(badMonth) === 0)
      )
    ) {
      continue;
    }

    // redo if year repeated
    if (
      // eslint-disable-next-line
      questions.some((item) => {
        const itemYear = item.substr(0, 4);

        if (dayNum > 78 && itemYear === "1900") {
          // should only do 1901 onwards
          // some old questions asked this year
          return true;
        }

        return (
          itemYear !== year + "" &&
          questions.some((item2) => {
            //console.log(item2.substr(0,4)+' === '+itemYear, item2.substr(0,4) === itemYear);
            return item !== item2 && item2.substr(0, 4) === itemYear;
          })
        );
      })
    ) {
      continue;
    }

    break;
  }

  return {
    failCount,
    badMonths,
    questions,
    _questions: questions.map((item) => {
      if (item.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const { year, month, date } = dayStrToParts(item);
        return [item.dateStr, year, month, date, dayOfWeek(year, month, date)];
      } else {
        return "foo";
      }
    }),
    _: dayNumToDateInfo(dayNum),
  };
};

const SCORE_UNATTEMPTED = 0;
const SCORE_CORRECT = 1;
const SCORE_WRONG = 2;

const KEY_DAILY = "KEY_DAILY";
const KEY_HISTORY = "HISTORY";

const MUM_WRONG_KEY = '_MUM_WRONG';

class Daily extends Component {
  state = {
    answeredCount: 0,
    questionIndex: null,
  };

  componentDidMount() {
    this.setDate();

    this.setState({
      history: this.getHistory(),
    });
  }

  getIsMum()
  {
    if (typeof this.__isMum === 'boolean') {
      return this.__isMum;
    }
    const history = this.getHistory();

    const d286 = history.find(item => item.dayNum === 286);
    const d284 = history.find(item => item.dayNum === 284);

    if (!d286 || !d284) {
      return false;
    }

    const r286 = historyItemToResults(d286.questions, d286.answers).join('');
    const r284 = historyItemToResults(d284.questions, d284.answers).join('');

    this.__isMum = (
      r286 === '000000000000' &&
      r284 === '110111101000'
    );

    return this.__isMum;
  }

  setDate() {
    const { dayNum, dayFrac, _isOneOffEarly } = getDayInfo();

    this.setState({
      dayNum,
      dayFrac,
      _isOneOffEarly,
    });

    try {
      const data = JSON.parse(window.localStorage.getItem(KEY_DAILY));

      if (data) {
        if (data.dayNum === dayNum) {
          this.setState(
            {
              answers: data.answers,
            },
            () => {
              const questionIndex = this.getQuestionIndex();
              if (questionIndex >= 0) {
                this.onStart();
              }
            }
          );
        }
      }
    } catch (ignore) {}
  }

  onStartClick = () => {
    const answers = [];
    for (let i = 0; i < 12; i++) {
      answers[i] = -1;
    }
    this.setAnswers(answers);
    this.onStart();
  };

  onStart = () => {
    if (!this.getIsMum()) {
      this.props.onStart(); // disables navigation
    }
  }

  setAnswers = (answers) => {
    const { dayNum } = this.state;

    const data = {
      dayNum,
      answers,
    };

    window.localStorage.setItem(KEY_DAILY, JSON.stringify(data));

    if (!answers.some((item) => item < 0)) {
      this.setFinalAnswers(dayNum, answers);
    }

    this.setState({
      answers,
    });
  };

  getHistory() {
    if (this.__history) {
      return this.__history;
    }
    try {
      const history = JSON.parse(window.localStorage.getItem(KEY_HISTORY));
      if (!(history instanceof Array)) {
        throw new Error();
      }
      this.__history = history;
      return history;
    } catch (err) {
      return [];
    }
  }

  setFinalAnswers(dayNum, answers) {
    const history = this.getHistory();
    if (!history.some((item) => item.dayNum === dayNum)) {
      const puzzle = getPuzzle(dayNum);
      history.push({
        dayNum,
        answers,
        questions: puzzle.questions,
      });
      window.localStorage.setItem(KEY_HISTORY, JSON.stringify(history));

      this.setState({
        history,
      });
    }
  }

  getQuestionIndex = () => {
    const { answers, justAnswered } = this.state;
    if (!answers) {
      return null;
    }
    const index = answers.findIndex((val) => val < 0);
    if (justAnswered) {
      return index < 0 ? answers.length - 1 : index - 1;
    }
    return index;
  };

  onAnswer = (wasCorrect, answer, duration, isSkip) => {
    const { answers } = this.state;
    const questionIndex = this.getQuestionIndex();

    if (!this._mumWrongCount) {
      this._mumWrongCount = 0;
    }

    let guessesLeft = 3 - this._mumWrongCount;
    if (this.getIsMum()) {
      const puzzle = getPuzzle(this.state.dayNum);
      const question = puzzle.questions[questionIndex];
      const correctAnswer = getAnswer(question);
      const isCorrect = answer === correctAnswer;

      const questionKey = this.state.dayNum+'~'+questionIndex;

      let oldAnswer = null;
      try {
        const [oldQuestionKey, storedAnswer] = window.localStorage.getItem(MUM_WRONG_KEY).split(':');
        if (oldQuestionKey === questionKey) {
          oldAnswer = parseInt(storedAnswer,10);
        }
      }
      catch (ignore) {}
      
      if (!isCorrect) {
        if (!oldAnswer) {
          window.localStorage.setItem(MUM_WRONG_KEY, questionKey+':'+answer);
        }
        let n = guessesLeft-1;
        if (n > 0) {
          alert('Wrong, but you have '+n+ ' more chance'+(n>1 ? 's' : '')+'!');
          this._mumWrongCount++;
          return;
        }
        else {
          alert('Wrong - sorry that was your last chance :(');
        }
      }
      else {
        if (typeof oldAnswer === 'number') {
          alert('Correct, but naughty! You are not allowed to cheat! This act of cheating has been reported to the FBI.');
        }
      }
      if (typeof oldAnswer === 'number') {
        answer = oldAnswer;
      }
    }

    window.localStorage.removeItem(MUM_WRONG_KEY)

    this._mumWrongCount = 0;

    const newAnswers = answers.slice();
    newAnswers[questionIndex] = answer;

    this.setState({
      justAnswered: true,
    });

    this.setAnswers(newAnswers);
  };

  onAfterAnswerWait = (wasCorrect, duration) => {
    this.setState(
      {
        justAnswered: false,
      },
      () => {
        if (this.getQuestionIndex() < 0) {
          this.props.onEnd();
        }
      }
    );
  };

  render() {
    const { answeredCount, answers, dayNum, history } = this.state;

    if (typeof dayNum !== "number") {
      return null;
    }

    const questionIndex = this.getQuestionIndex();

    const puzzle = getPuzzle(dayNum);
    const question =
      questionIndex === null ? null : puzzle.questions[questionIndex];

    const started = questionIndex !== null;

    const displayFormatRnd =
      started && getDisplayFormatRnd(dayNum, questionIndex);

    let lifeCount = 3;

    const results =
      answers &&
      answers.map((answer, index) => {
        if (answer < 0) {
          return SCORE_UNATTEMPTED;
        }
        const questionDateStr = puzzle.questions[index];
        //console.log(questionDateStr, getAnswer(questionDateStr), answer, answers);
        const correctAnswer = getAnswer(questionDateStr);
        if (answer === correctAnswer) {
          return SCORE_CORRECT;
        } else {
          lifeCount--;
          return SCORE_WRONG;
        }
      });

    return (
      <QuizLayout className="Daily">
        {!started ? (
          <div>
            <Logo />
            <h2>{this.getIsMum() ? 'Mum ' : ''}Day: {dayNum + ""}</h2>
            <p>
              Time remaining:{" "}
              <Countdown _isOneOffEarly={this.state._isOneOffEarly} />
            </p>

            <AboutGame startOpened={!history || !history.length} />

            <button
              type="button"
              onClick={this.onStartClick}
              className="-start"
            >
              Start
            </button>

            <br />
            <br />
            {history && (
              <>
                <HistoryStreak dayNum={dayNum - 1} history={history} />
                <HistoryList dayNum={dayNum - 1} history={history} />
              </>
            )}
          </div>
        ) : question ? (
          [
            <Question
              key={questionIndex}
              dateStr={question}
              displayFormatRnd={displayFormatRnd}
              hasSkip={false}
              onAnswer={this.onAnswer}
              disabled={this.state.disabled}
              onAfterAnswerWait={this.onAfterAnswerWait}
            >
              <b>Started</b>

              {results && (
                <Results
                  dayNum={dayNum}
                  results={results}
                  history={history}
                  lifeCount={lifeCount}
                  answeredCount={answeredCount}
                  showCopyButton={!question}
                />
              )}

              <p>
                Question {questionIndex + 1} of {puzzle.questions.length}
              </p>
            </Question>,
          ]
        ) : (
          <>
            <div className="-countdown">
              <p>
                Time until next game:{" "}
                <Countdown _isOneOffEarly={this.state._isOneOffEarly} />
              </p>
              {this.state._isOneOffEarly && (
                <p style={{ padding: "0 20px" }}>
                  <i>
                    Note: normally there is one game per day, but since today is
                    "Day 1", and due to your timezone, there is a one-off extra
                    wait-time, sorry!
                  </i>
                </p>
              )}
              <AboutGame />
            </div>

            <b>{this.getIsMum() ? 'Mum ' : ''}Day {dayNum}</b>

            {results && (
              <Results
                dayNum={dayNum}
                results={results}
                history={history}
                lifeCount={lifeCount}
                answeredCount={answeredCount}
                showCopyButton={!question}
              >
                {history && <HistoryStreak dayNum={dayNum} history={history} />}
              </Results>
            )}

            <QuestionAnswersOutput
              dayNum={dayNum}
              questions={puzzle.questions}
              answers={answers}
            />

            {history && (
              <>
                <br />
                <b>History:</b>
                <HistoryList dayNum={dayNum} history={history} />
              </>
            )}
          </>
        )}
      </QuizLayout>
    );
  }
}

const AboutGame = ({ startOpened }) => {
  const [isOpen, setOpen] = useState(startOpened);
  const onButtonClick = (evt) => {
    evt.preventDefault();
    setOpen(!isOpen);
  };
  if (!isOpen) {
    return (
      <button className="-about --closed" type="button" onClick={onButtonClick}>
        ?
      </button>
    );
  }

  const isUs = getIsUsa();

  return (
    <div className="-about --opened">
      <button type="button" onClick={onButtonClick}>
        &times;
      </button>
      <b onClick={onButtonClick}>About the Daily Game</b>
      <p>
        You will get 12 questions. They begin close to the year 2023, and
        progressively get further away,
        up&nbsp;to&nbsp;1901&#8288;&mdash;&#8288;2099.
      </p>
      <p>
        The first ten questions are to pick the weekday from a date, and the
        last two questions are to pick the date given the{" "}
        <i>Nth&nbsp;weekday</i> of a month.
      </p>
      <p>
        You only get one chance for each question, and you won't be able to
        access the other tabs to "cheat", so make sure you have{" "}
        {isUs ? "practiced" : "practised"} first!
      </p>
    </div>
  );
};

const Question = (props) =>
  props.dateStr.indexOf(":") < 0 ? (
    <DayQuestion {...props} />
  ) : (
    <NthQuestion {...props} />
  );

const QuestionAnswersOutput = ({ dayNum, questions, answers }) => {
  return (
    <div className="-output" dir={getLocaleIsRtl() ? "rtl" : null}>
      {questions.map((questionDayStr, index) => {
        const displayFormatRnd = getDisplayFormatRnd(dayNum, index);
        const correctAnswer = getAnswer(questionDayStr);
        const isCorrect = answers[index] === correctAnswer;
        return (
          <div key={index} className={isCorrect ? "--correct" : "--wrong"}>
            {questionDayStr.indexOf(":") < 0 ? (
              <>
                <div className="-question">
                  {formatDateStr(questionDayStr, displayFormatRnd)}
                </div>
                <div className="-answer">
                  {getLocaleShortWeekday(answers[index])}
                </div>
              </>
            ) : (
              <>
                <div className="-question">
                  {describeNthQuestion(questionDayStr, true)}
                </div>
                <div className="-answer">{answers[index]}</div>
              </>
            )}
            <div className="-icon">{isCorrect ? EMOJI_TICK : EMOJI_CROSS}</div>
          </div>
        );
      })}
    </div>
  );
};

const calculateScore = (questions, answers) => {
  return (
    questions &&
    questions.filter(
      (question, index) => getAnswer(question) === answers[index]
    ).length
  );
};

const historyItemToResults = (questions, answers) => {
  return (
    (questions &&
      answers &&
      questions.map((question, index) =>
        getAnswer(question) === answers[index] ? 1 : 0
      )) ||
    null
  );
};

const HistoryList = ({ dayNum, history }) => {
  const past = [];
  for (let i = 1; i <= dayNum; i++) {
    const historyItem = history.find((item) => item.dayNum === i);
    if (!historyItem && !past.length) {
      continue;
    }
    const results = historyItem
      ? historyItemToResults(historyItem.questions, historyItem.answers)
      : null;
    const score = (results && results.reduce((sum, val) => sum + val, 0)) || 0;
    const stars = Math.max(score - 9, 0);
    past.push({
      dayNum: i,
      results,
      stars,
    });
  }

  past.reverse();

  return (
    <ul className="History-list" dir={getLocaleIsRtl() ? "rtl" : null}>
      {past.map((pastItem) => (
        <li key={pastItem.dayNum}>
          <em>Day {pastItem.dayNum}</em>{" "}
          {pastItem.results
            ? pastItem.results.map((v) => (v ? EMOJI_CORRECT : EMOJI_WRONG))
            : " ... "}
          {pastItem.stars
            ? new Array(pastItem.stars).fill(EMOJI_LIFE).join("")
            : null}
        </li>
      ))}
    </ul>
  );
};

const calculateStreak = (dayNum, history) => {
  let streak = 0;
  for (let i = dayNum; i >= 1; i--) {
    const historyItem = history.find((item) => item.dayNum === i);
    if (historyItem) {
      streak++;
    } else {
      break;
    }
  }
  return streak;
};

const calculatePerfectStreak = (dayNum, history) => {
  let perfectStreak = 0;
  for (let i = dayNum; i >= 1; i--) {
    const historyItem = history.find((item) => item.dayNum === i);
    if (
      historyItem &&
      calculateScore(historyItem.questions, historyItem.answers) === 12
    ) {
      perfectStreak++;
    } else {
      break;
    }
  }
  return perfectStreak;
};

const HistoryStreak = ({ dayNum, history }) => {
  return (
    <div>
      <b>
        Streak: {numToEmoji(calculateStreak(dayNum, history))}
        <br />
        Perfect Streak: {numToEmoji(calculatePerfectStreak(dayNum, history))}
      </b>
    </div>
  );
};

const numToEmoji = (num) => {
  if (num === 10) {
    return EMOJI_BOX_NUM_10;
  }
  return (num + "")
    .split("")
    .map((digit) => (digit.match(/^\d$/) ? EMOJI_BOX_NUMS[digit] : digit))
    .join("");
};

class Results extends Component {
  state = {
    copied: false,
  };

  componentWillUnmount() {
    clearTimeout(this.timeout);
  }

  onCopyClick = (evt) => {
    evt.preventDefault();

    const { dayNum, history } = this.props;

    const streak = calculateStreak(dayNum, history);
    const perfectStreak = calculatePerfectStreak(dayNum, history);

    const text =
      "#weekle - Day " +
      dayNum +
      "\n" +
      this.getText() +
      "\n" +
      "Streak: " +
      numToEmoji(streak) +
      "\n" +
      (perfectStreak > 0
        ? "Perfect Streak: " + numToEmoji(perfectStreak) + "\n"
        : "") +
      "https://benjoffe.com/weekle";

    navigator.clipboard.writeText(text).then(
      () => {
        this.setState(
          {
            copied: true,
          },
          this.waitAfterCopy
        );
      },
      (err) => {
        console.error(err);
        alert("Unable to copy text! Please copy manually.");
      }
    );
  };

  waitAfterCopy = () => {
    this.timeout = setTimeout(this.clearCopied, 1000);
  };

  clearCopied = () => {
    this.setState({
      copied: false,
    });
  };

  getText() {
    let { results, lifeCount, answeredCount } = this.props;

    const isDark = false;
    const emptyChar = isDark ? EMOJI_EMPTY_BLACK : EMOJI_EMPTY_WHITE;
    const skippedChar = EMOJI_EMPTY_ORANGE;
    const CHARS = [null, EMOJI_CORRECT, EMOJI_WRONG];

    if (typeof answeredCount !== "number") {
      answeredCount = results.length;
    }

    return (
      (lifeCount > 0
        ? new Array(lifeCount).fill(EMOJI_LIFE).join("")
        : new Array(1 - lifeCount).fill(EMOJI_FAIL).join("")) +
      "\n" +
      results
        .map((result, index) =>
          !CHARS[result]
            ? index < answeredCount
              ? skippedChar
              : emptyChar
            : CHARS[result]
        )
        .join("")
    );
  }

  render() {
    const { showCopyButton, children } = this.props;
    const { copied } = this.state;

    return (
      <div className="Results">
        <div className="-icons">{this.getText()}</div>

        {children}

        {showCopyButton && (
          <button
            type="button"
            onClick={this.onCopyClick}
            className={copied ? "--active" : ""}
          >
            Copy to Clipboard
          </button>
        )}

        <em>{copied && <>Copied to Clipboard!</>}</em>
      </div>
    );
  }
}

class Countdown extends Component {
  state = {};

  componentMount() {
    this.setTime();
  }

  componentWillUnmount() {
    clearTimeout(this.timeout);
  }

  componentDidMount() {
    console.log("tick");
    this.tick();
  }

  setTime() {
    const { dayFrac } = getDayInfo();

    this.setState({
      dayFrac,
    });
  }

  tick = () => {
    this.setTime();

    const S = 1000;
    let t = 1000 - (Date.now() % S);
    if (t < S / 10) {
      t += S;
    }

    this.timeout = setTimeout(this.tick, S);
  };

  render() {
    const { dayFrac } = this.state;
    const { _isOneOffEarly } = this.props;

    if (typeof dayFrac !== "number") {
      return null;
    }

    const H = (1 - dayFrac) * 24 + (_isOneOffEarly ? 24 : 0);
    const h = Math.floor(H);
    const m = Math.floor((H % 1) * 60);
    const s = Math.floor((H * 60 * 60) % 60);

    return (
      <span className="Countdown">
        {pad2(Math.floor(h))}h {pad2(m)}m {pad2(s)}s
      </span>
    );
  }
}

export default Daily;
